import React, { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import * as authService from '@mepro/services/authService'
import * as walletService from '@mepro/services/walletService'
import type { WalletData } from '@mepro/services/walletService'
import { useLotteryResults } from '@mepro/hooks/useLotteryResults'
import {
  displayValue,
  getGovernmentThreeDownList,
  getRoundDrawLabel,
  getWinningNumbers,
  hasAnyResult,
  isRoundCancelled,
} from '@mepro/utils/resultDisplayUtils'
import { getRoundImage, canRoundAcceptBet, isRoundNotYetOpen } from '@mepro/services/lotteryService'
import type { TenantThemeConfig } from '../types/theme'
import { getThemeById } from '../config/themes'
import './HomePage.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface HomePageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
  /** full = หน้าแรก · results = tab ผลหวย (การ์ดชุดเดียวกับหน้าแรก + เลือกวันที่) */
  layout?: 'full' | 'results'
}

interface SectionCard {
  id: string
  name: string
  flag: string
  drawLabel?: string
  a: string
  b: string
  labelA?: string
  labelB?: string
  /** wait=รอผล · open=เปิดรับ · closed=ปิดรับแทง · ready=ประกาศผลแล้ว */
  tone?: 'wait' | 'open' | 'closed' | 'ready' | 'cancelled'
  /** ใกล้ปิดรับ — class wip_wup ตาม wipWupMinutes */
  wip?: boolean
}

interface SectionDef {
  id: string
  title: string
  dateLabel?: string
  tone?: 'gold' | 'dark'
  emptyHint?: string
  cards: SectionCard[]
  /** set = เลขชุด 4 ตัว · yeekee = กริดรอบยี่กี */
  kind?: 'pair' | 'set' | 'yeekee'
}

interface ShowcaseSlide {
  id: string
  category: string
  title: string
  meta: string
  icon: string
  variant: 'gov' | 'pair' | 'set' | 'yeekee'
  live: boolean
  wip: boolean
  rows: { label: string; value: string }[]
}

function sectionCardToShowcaseSlide(
  section: SectionDef,
  card: SectionCard,
  getAssetUrl: (path: string) => string,
  fallbackFlag = '/img/flag/th.png'
): ShowcaseSlide {
  const isSet = section.kind === 'set'
  const rows = isSet
    ? [{ label: card.labelA || 'เลขชุด 4 ตัว', value: card.a }]
    : [
        { label: card.labelA || '3 บน', value: card.a },
        ...(card.b ? [{ label: card.labelB || '2 ล่าง', value: card.b }] : []),
      ]
  return {
    id: `${section.id}-${card.id}`,
    category: section.title,
    title: card.name,
    meta: card.drawLabel || '—',
    icon: resolveShowcaseIcon(card.flag, fallbackFlag, getAssetUrl),
    variant: section.kind === 'set' ? 'set' : section.kind === 'yeekee' ? 'yeekee' : 'pair',
    live: card.tone === 'ready',
    wip: card.wip ?? false,
    rows,
  }
}

function resolveShowcaseIcon(
  icon: string | undefined,
  fallbackPath: string,
  getAssetUrl: (path: string) => string
): string {
  const trimmed = icon?.trim()
  if (trimmed) return trimmed
  return getAssetUrl(fallbackPath)
}

type RoundLike = {
  id?: string
  _id?: string
  huayName?: string
  name?: string
  roundNumber?: string
  betStartTime?: string
  betEndTime?: string
  status?: string
  isOpen?: boolean
  results?: { winningNumbers?: Record<string, string> }
  huayImageURL?: string
  yeekee?: {
    result?: { threeDigitTop?: string; twoDigitDown?: string; twoDigitTop?: string }
  }
} | null

function todayBangkokYYYYMMDD(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

function formatYeekeeRoundLabel(roundNumber: string | undefined): string {
  if (!roundNumber) return '0'
  const parts = roundNumber.split('-')
  const suffix = parts[parts.length - 1]
  if (/^\d+$/.test(suffix)) return String(parseInt(suffix, 10))
  return roundNumber
}

function getYeekeeTimeLabel(round: RoundLike): string {
  if (!round?.betEndTime) return '-'
  return new Date(round.betEndTime).toLocaleTimeString('th-TH', {
    timeZone: 'Asia/Bangkok',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function isCancelled(round: RoundLike): boolean {
  return isRoundCancelled(round as never)
}

/** สถานะการ์ดผล — แนวเดียวกับ HTD ready / closed / wait */
function getCardTone(round: RoundLike): 'wait' | 'closed' | 'ready' | 'cancelled' {
  if (!round) return 'wait'
  if (isCancelled(round)) return 'cancelled'
  if (hasAnyResult(round as never)) return 'ready'

  const status = String(round.status || '').toLowerCase()
  const now = Date.now()
  const betEnd = round.betEndTime ? new Date(round.betEndTime).getTime() : NaN
  const pastEnd = !Number.isNaN(betEnd) && now >= betEnd
  const stillOpen = status === 'open' && round.isOpen !== false && !pastEnd

  if (stillOpen) return 'wait'
  return 'closed'
}

function isYeekeeLikeRound(round: RoundLike): boolean {
  if (!round) return false
  const s = `${round.huayName || ''}${round.name || ''}${round.roundNumber || ''}`
  return /ยี่กี|ยีกี่|yeekee/i.test(s)
}

function getWipThreshold(round: RoundLike, wipYeekee: number, wipOther: number): number {
  return isYeekeeLikeRound(round) ? wipYeekee : wipOther
}

/** ใกล้ปิดรับ — ตรง user-htd HtdLotteryResults / lobby listall */
function isRoundWipWup(round: RoundLike, thresholdMinutes: number, now: number): boolean {
  if (!round || isCancelled(round)) return false
  if (!round.betEndTime) return false
  try {
    const notYetOpen = isRoundNotYetOpen(round as never)
    const active = canRoundAcceptBet(round as never) && !notYetOpen
    if (!active) return false
    const minutesRemaining = (new Date(round.betEndTime).getTime() - now) / (60 * 1000)
    return minutesRemaining <= thresholdMinutes && minutesRemaining > 0
  } catch {
    return false
  }
}

function num(round: RoundLike, key: 'first' | 'threeUp' | 'twoDown' | 'threeDown' | 'twoUp' | 'fourUp'): string {
  const cancelled = isCancelled(round)
  const has = hasAnyResult(round as never)
  const w = getWinningNumbers(round as never)
  return displayValue(w[key] || '', { isCancelled: cancelled, hasResult: has })
}

function yeekeeDigits(round: RoundLike): { threeUp: string; twoDown: string } {
  if (!round) return { threeUp: '', twoDown: '' }
  const wn = getWinningNumbers(round as never)
  const res = (
    round as {
      yeekee?: { result?: { threeDigitTop?: string; twoDigitDown?: string; twoDigitTop?: string } }
    }
  ).yeekee?.result
  return {
    threeUp: wn.threeUp || res?.threeDigitTop || '',
    twoDown: wn.twoDown || res?.twoDigitDown || res?.twoDigitTop || '',
  }
}

/** HTD-style pair widget: no round → "—" / ยังไม่มีงวด (ไม่โชว์ รอผลปลอม) */
function buildPairWidget(
  round: RoundLike,
  fallbackTitle: string,
  getAssetUrl: (path: string) => string,
  fallbackFlag: string,
  selectedDate: string
) {
  const hasRound = Boolean(round)
  if (!hasRound) {
    return {
      title: fallbackTitle,
      subtitle: 'ยังไม่มีงวด',
      icon: getAssetUrl(fallbackFlag),
      three: '—',
      two: '—',
      empty: true,
      live: false,
      cancelled: false,
      closed: false,
    }
  }
  const cancelled = isCancelled(round)
  const has = hasAnyResult(round as never)
  const tone = getCardTone(round)
  const wn = getWinningNumbers(round as never)
  const disp = (v: string) => displayValue(v, { isCancelled: cancelled, hasResult: has })
  return {
    title: roundTitle(round, fallbackTitle),
    subtitle:
      cancelled ? 'ยกเลิก' : tone === 'closed' ? 'ปิดรับแทง' : getRoundDrawLabel(round as never, selectedDate),
    icon: flagFromRound(round, getAssetUrl, fallbackFlag),
    three: disp(wn.threeUp),
    two: disp(wn.twoDown),
    empty: false,
    live: has && !cancelled,
    cancelled,
    closed: tone === 'closed',
  }
}

function roundKey(round: RoundLike, fallback: string): string {
  return String(round?.id || round?._id || round?.huayName || fallback)
}

function roundTitle(round: RoundLike, fallback: string): string {
  return String(round?.huayName || round?.name || fallback)
}

function flagFromRound(
  round: RoundLike,
  getAssetUrl: (path: string) => string,
  fallbackPath = '/img/flag/th.png'
): string {
  if (!round) return getAssetUrl(fallbackPath)
  try {
    const src = getRoundImage(round as never)
    if (typeof src === 'string' && /^https?:\/\//i.test(src)) return src
    if (typeof src === 'string' && src) return getAssetUrl(src)
  } catch {
    // ignore — use fallback
  }
  return getAssetUrl(fallbackPath)
}

function mapCards(
  rounds: RoundLike[],
  fallbackName: string,
  getAssetUrl: (path: string) => string,
  options?: {
    fallbackFlag?: string
    selectedDate?: string
    kind?: 'pair' | 'set'
    now?: number
    wipYeekee?: number
    wipOther?: number
  }
): SectionCard[] {
  const fallbackFlag = options?.fallbackFlag || '/img/flag/th.png'
  const selectedDate = options?.selectedDate
  const kind = options?.kind || 'pair'
  const now = options?.now ?? Date.now()
  const wipYeekee = options?.wipYeekee ?? 15
  const wipOther = options?.wipOther ?? 15
  return (rounds || []).slice(0, 24).map((r, i) => {
    const tone = getCardTone(r)
    const wip = isRoundWipWup(r, getWipThreshold(r, wipYeekee, wipOther), now)
    if (kind === 'set') {
      return {
        id: roundKey(r, `${fallbackName}-${i}`),
        name: roundTitle(r, fallbackName),
        flag: flagFromRound(r, getAssetUrl, fallbackFlag),
        drawLabel: selectedDate ? getRoundDrawLabel(r as never, selectedDate) : undefined,
        a: num(r, 'fourUp'),
        b: '',
        labelA: 'เลขชุด 4 ตัว',
        labelB: '',
        tone,
        wip,
      }
    }
    return {
      id: roundKey(r, `${fallbackName}-${i}`),
      name: roundTitle(r, fallbackName),
      flag: flagFromRound(r, getAssetUrl, fallbackFlag),
      drawLabel: selectedDate ? getRoundDrawLabel(r as never, selectedDate) : undefined,
      a: num(r, 'threeUp'),
      b: num(r, 'twoDown'),
      labelA: '3 ตัวบน',
      labelB: '2 ตัวล่าง',
      tone,
      wip,
    }
  })
}

/** ยี่กี VIP — แสดงครบทุกรอบแบบ HTD (รอบ + เวลา + ผลจาก yeekee.result) */
function mapYeekeeVipCards(
  rounds: RoundLike[],
  options?: { now?: number; wipYeekee?: number; wipOther?: number }
): SectionCard[] {
  const now = options?.now ?? Date.now()
  const wipYeekee = options?.wipYeekee ?? 15
  const wipOther = options?.wipOther ?? 15
  return [...(rounds || [])]
    .sort(
      (a, b) =>
        new Date(a?.betStartTime || a?.betEndTime || 0).getTime() -
        new Date(b?.betStartTime || b?.betEndTime || 0).getTime()
    )
    .map((r, i) => {
      const res = r?.yeekee?.result
      const wn = getWinningNumbers(r as never)
      const threeRaw = res?.threeDigitTop || wn.threeUp || ''
      const twoRaw = res?.twoDigitDown || res?.twoDigitTop || wn.twoDown || ''
      const cancelled = isCancelled(r)
      const status = String(r?.status || '').toLowerCase()
      const betStart = r?.betStartTime ? new Date(r.betStartTime).getTime() : NaN
      const betEnd = r?.betEndTime ? new Date(r.betEndTime).getTime() : NaN
      const isOpen = status === 'open' && r?.isOpen !== false
      const isFuture = isOpen && !Number.isNaN(betStart) && now < betStart
      const isInBetWindow =
        isOpen && !Number.isNaN(betStart) && !Number.isNaN(betEnd) && now >= betStart && now <= betEnd
      const isClosed = !isFuture && (!isOpen || (!Number.isNaN(betEnd) && now > betEnd)) && !cancelled
      const hasResult = !cancelled && !isFuture && !!(threeRaw || twoRaw)
      const wip = isRoundWipWup(r, getWipThreshold(r, wipYeekee, wipOther), now)

      let tone: SectionCard['tone'] = 'wait'
      if (cancelled) tone = 'cancelled'
      else if (hasResult) tone = 'ready'
      else if (isClosed) tone = 'closed'
      else if (isInBetWindow) tone = 'open'
      else tone = 'wait'

      const timeStr = getYeekeeTimeLabel(r)

      return {
        id: roundKey(r, `yk-vip-${i}`),
        name: `รอบ ${formatYeekeeRoundLabel(r?.roundNumber)}`,
        flag: '',
        drawLabel:
          tone === 'closed' ? 'ปิดรับแทง' : timeStr !== '-' ? `${timeStr} น.` : undefined,
        a: cancelled ? 'ยกเลิก' : isFuture ? 'รอผล' : threeRaw || 'รอผล',
        b: cancelled ? 'ยกเลิก' : isFuture ? 'รอผล' : twoRaw || 'รอผล',
        labelA: '3 บน',
        labelB: '2 ล่าง',
        tone,
        wip,
      }
    })
}

const HomePage: React.FC<HomePageProps> = ({ themeConfig, assetConfig, layout = 'full' }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isResultsLayout = layout === 'results'
  const authenticated = authService.isAuthenticated()
  const user = authenticated ? authService.getCurrentUser() : null
  const isLotteryEnabled = themeConfig.settings?.lotteryEnabled !== false
  const isCasinoEnabled = themeConfig.settings?.casinoEnabled !== false
  const [resultsDate, setResultsDate] = useState(todayBangkokYYYYMMDD)
  const selectedDate = isResultsLayout ? resultsDate : todayBangkokYYYYMMDD()
  const themeFolder = getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue'

  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL
      ? `${assetConfig.CDN_BASE_URL}/${themeFolder}`
      : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }

  const {
    loading: resultsLoading,
    poolError,
    latestGovernmentRound,
    latestYeekeeRound,
    recentYeekeeRounds,
    latestEgovRound,
    latestGsbRound,
    latestSetRounds,
    latestThaiStockRounds,
    latestForeignRounds,
    latestForeignStockRounds,
    latestVipRounds,
    latestVisaRounds,
    allYeekeeVipRounds,
  } = useLotteryResults(selectedDate, { pollingIntervalMs: 20_000 }, themeConfig.tenantId)

  const [username, setUsername] = useState(
    () => localStorage.getItem('savedUsername') || ''
  )
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem('rememberMe') !== 'false'
  )
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [now, setNow] = useState(() => Date.now())
  const [walletData, setWalletData] = useState<WalletData | null>(null)
  const [points, setPoints] = useState(0)
  const [loadingWallet, setLoadingWallet] = useState(false)

  const wipWupMinutes = themeConfig.settings?.wipWupMinutes ?? 15
  const wipWupMinutesOther = themeConfig.settings?.wipWupMinutesOther ?? 15

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 15_000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    if (!authenticated) {
      setWalletData(null)
      setPoints(0)
      return
    }
    setLoadingWallet(true)
    Promise.all([walletService.getWallet(), authService.fetchUserInfoSummary()])
      .then(([walletRes, summary]) => {
        if (walletRes.success && walletRes.data) setWalletData(walletRes.data)
        if (summary) setPoints(summary.points ?? 0)
      })
      .finally(() => setLoadingWallet(false))
  }, [authenticated])

  const topWidgets = useMemo(() => {
    const gov = latestGovernmentRound as RoundLike
    const yeekee = latestYeekeeRound as RoundLike
    const egov = latestEgovRound as RoundLike
    const gsb = latestGsbRound as RoundLike

    const govHasRound = Boolean(gov)
    const govCancelled = isCancelled(gov)
    const govHas = hasAnyResult(gov as never)
    const govWn = getWinningNumbers(gov as never)
    const govThreeDown = getGovernmentThreeDownList(gov as never)
    const dispGov = (v: string) =>
      displayValue(v, { isCancelled: govCancelled, hasResult: govHas })

    let govBottomPending = ''
    if (!govHasRound) govBottomPending = '—'
    else if (govCancelled) govBottomPending = 'ยกเลิก'
    else if (!(govHas && govThreeDown.length > 0)) govBottomPending = 'รอผล'

    const yDigits = yeekeeDigits(yeekee)
    const yeekeeHasRound = Boolean(yeekee)
    const yeekeeCancelled = isCancelled(yeekee)
    const yeekeeHas =
      hasAnyResult(yeekee as never) || !!(yDigits.threeUp || yDigits.twoDown)
    const dispYeekee = (v: string) =>
      displayValue(v, { isCancelled: yeekeeCancelled, hasResult: yeekeeHas })

    const yeekeeRecent = yeekeeHasRound
      ? (recentYeekeeRounds as RoundLike[])
          .filter((r) => r && (r._id || r.id) !== (yeekee?._id || yeekee?.id))
          .slice(0, 6)
          .map((r) => {
            const digits = yeekeeDigits(r)
            const cancelled = isCancelled(r)
            const hasResult = !!(digits.threeUp || digits.twoDown)
            return {
              id: String(r?._id || r?.id || r?.roundNumber || ''),
              label: formatYeekeeRoundLabel(r?.roundNumber),
              time: getYeekeeTimeLabel(r),
              three: displayValue(digits.threeUp, { isCancelled: cancelled, hasResult }),
              two: displayValue(digits.twoDown, { isCancelled: cancelled, hasResult }),
              ready: hasResult && !cancelled,
            }
          })
      : []

    const roundWip = (round: RoundLike) =>
      isRoundWipWup(round, getWipThreshold(round, wipWupMinutes, wipWupMinutesOther), now)

    return {
      gov: {
        title: govHasRound ? roundTitle(gov, 'หวยรัฐบาลไทย') : 'หวยรัฐบาลไทย',
        subtitle: !govHasRound
          ? 'ยังไม่มีงวด'
          : govCancelled
            ? 'ยกเลิก'
            : getCardTone(gov) === 'closed'
              ? 'ปิดรับแทง'
              : getRoundDrawLabel(gov as never, selectedDate),
        icon: flagFromRound(gov, getAssetUrl, '/img/flag/th.png'),
        first: govHasRound ? dispGov(govWn.first) : '—',
        threeUp: govHasRound ? dispGov(govWn.threeUp) : '—',
        twoDown: govHasRound ? dispGov(govWn.twoDown) : '—',
        bottomPending: govBottomPending,
        bottomNumbers: govBottomPending ? ([] as string[]) : govThreeDown,
        empty: !govHasRound,
        live: govHasRound && govHas && !govCancelled,
        cancelled: govCancelled,
        closed: govHasRound && getCardTone(gov) === 'closed',
        wip: govHasRound ? roundWip(gov) : false,
      },
      yeekee: yeekeeHasRound
        ? {
            title: 'จับยี่กี',
            subtitle:
              getCardTone(yeekee) === 'closed'
                ? 'ปิดรับแทง'
                : `รอบที่ ${formatYeekeeRoundLabel(yeekee?.roundNumber)} · ${getYeekeeTimeLabel(yeekee)}`,
            roundLabel: formatYeekeeRoundLabel(yeekee?.roundNumber),
            timeLabel: getYeekeeTimeLabel(yeekee),
            icon: flagFromRound(yeekee, getAssetUrl, '/img/flag/th.png'),
            three: dispYeekee(yDigits.threeUp),
            two: dispYeekee(yDigits.twoDown),
            empty: false,
            live: yeekeeHas && !yeekeeCancelled,
            cancelled: yeekeeCancelled,
            closed: getCardTone(yeekee) === 'closed',
            wip: roundWip(yeekee),
            recent: yeekeeRecent,
          }
        : {
            title: 'จับยี่กี',
            subtitle: 'ยังไม่มีงวด',
            roundLabel: '',
            timeLabel: '',
            icon: getAssetUrl('/img/flag/th.png'),
            three: '—',
            two: '—',
            empty: true,
            live: false,
            cancelled: false,
            closed: false,
            wip: false,
            recent: [] as typeof yeekeeRecent,
          },
      egov: {
        ...buildPairWidget(egov, 'หวยธกส.', getAssetUrl, '/img/flag/egov.png', selectedDate),
        wip: egov ? roundWip(egov) : false,
      },
      gsb: {
        ...buildPairWidget(gsb, 'หวยออมสิน', getAssetUrl, '/img/flag/gsb.png', selectedDate),
        wip: gsb ? roundWip(gsb) : false,
      },
    }
  }, [
    latestGovernmentRound,
    latestYeekeeRound,
    recentYeekeeRounds,
    latestEgovRound,
    latestGsbRound,
    selectedDate,
    assetConfig.CDN_BASE_URL,
    themeFolder,
    now,
    wipWupMinutes,
    wipWupMinutesOther,
  ])

  /** ลำดับหมวดเดียวกับ user-htd HtdLotteryResults */
  const sections = useMemo<SectionDef[]>(() => {
    const mapOpts = {
      selectedDate,
      now,
      wipYeekee: wipWupMinutes,
      wipOther: wipWupMinutesOther,
    }
    const list: SectionDef[] = [
      {
        id: 'set',
        title: 'หวยเลขชุด',
        dateLabel: 'งวด',
        kind: 'set',
        cards: mapCards(latestSetRounds as RoundLike[], 'หวยชุด', getAssetUrl, {
          ...mapOpts,
          kind: 'set',
          fallbackFlag: '/img/flag/th.png',
        }),
      },
      {
        id: 'thai-stock',
        title: 'หวยหุ้นไทย',
        dateLabel: 'งวด',
        cards: mapCards(latestThaiStockRounds as RoundLike[], 'หุ้นไทย', getAssetUrl, {
          ...mapOpts,
          fallbackFlag: '/img/flag/th.png',
        }),
      },
      {
        id: 'foreign',
        title: 'หวยต่างประเทศ',
        dateLabel: 'งวด',
        cards: mapCards(latestForeignRounds as RoundLike[], 'ต่างประเทศ', getAssetUrl, {
          ...mapOpts,
          fallbackFlag: '/img/flag/la.png',
        }),
      },
      {
        id: 'stock',
        title: 'หวยหุ้น',
        dateLabel: 'งวด',
        cards: mapCards(latestForeignStockRounds as RoundLike[], 'หวยหุ้น', getAssetUrl, {
          ...mapOpts,
          fallbackFlag: '/img/flag/la.png',
        }),
      },
      {
        id: 'vip',
        title: 'หวย VIP',
        dateLabel: 'งวด',
        cards: mapCards(latestVipRounds as RoundLike[], 'หวย VIP', getAssetUrl, {
          ...mapOpts,
          fallbackFlag: '/img/flag/la.png',
        }),
      },
      {
        id: 'visa',
        title: 'หวย VISA',
        dateLabel: 'งวด',
        cards: mapCards(latestVisaRounds as RoundLike[], 'หวย VISA', getAssetUrl, {
          ...mapOpts,
          fallbackFlag: '/img/flag/la.png',
        }),
      },
      {
        id: 'yeekee-vip',
        title: 'ยี่กี VIP',
        dateLabel: 'งวด',
        tone: 'dark',
        kind: 'yeekee',
        cards: mapYeekeeVipCards(allYeekeeVipRounds as RoundLike[], {
          now,
          wipYeekee: wipWupMinutes,
          wipOther: wipWupMinutesOther,
        }),
      },
    ]
    return list
  }, [
    latestSetRounds,
    latestThaiStockRounds,
    latestForeignRounds,
    latestForeignStockRounds,
    latestVipRounds,
    latestVisaRounds,
    allYeekeeVipRounds,
    selectedDate,
    assetConfig.CDN_BASE_URL,
    themeFolder,
    now,
    wipWupMinutes,
    wipWupMinutesOther,
  ])

  const visibleSections = useMemo(
    () =>
      resultsLoading
        ? sections
        : sections.filter((section) => section.cards.length > 0),
    [sections, resultsLoading]
  )

  /** สไลด์หมุน showcase — ยี่กี 1 รอบ + หวยเด่น + เน้นต่างประเทศ/VIP (~20 รายการ) */
  const showcaseSlides = useMemo(() => {
    const SHOWCASE_LIMIT = 20
    const slides: ShowcaseSlide[] = []
    const seen = new Set<string>()
    const push = (slide: ShowcaseSlide) => {
      if (seen.has(slide.id) || slides.length >= SHOWCASE_LIMIT) return
      seen.add(slide.id)
      slides.push({
        ...slide,
        icon: resolveShowcaseIcon(slide.icon, '/img/flag/th.png', getAssetUrl),
      })
    }

    const pickSectionCards = (section: SectionDef, max: number) => {
      if (!section.cards.length || section.kind === 'yeekee') return []
      const ready = section.cards.filter((c) => c.tone === 'ready')
      const pool = ready.length > 0 ? ready : section.cards
      return pool.slice(0, max)
    }

    const sectionById = (id: string) => sections.find((s) => s.id === id)

    const yeekee = latestYeekeeRound as RoundLike
    const yDigits = yeekeeDigits(yeekee)
    const yeekeeLatestHasResult =
      Boolean(yeekee) &&
      !isCancelled(yeekee) &&
      (hasAnyResult(yeekee as never) || !!(yDigits.threeUp || yDigits.twoDown))

    // ใช้รอบยี่กีล่าสุดของวันนี้เสมอ — อย่าดึงผลรอบก่อนมาแทนเมื่อรอบปัจจุบันปิดรับแล้วรอผล
    if (yeekee) {
      const resultDigits = yeekeeDigits(yeekee)
      const cancelled = isCancelled(yeekee)
      const hasResult = yeekeeLatestHasResult
      const disp = (v: string) =>
        displayValue(v, { isCancelled: cancelled, hasResult })
      push({
        id: 'featured-yeekee-result',
        category: 'หวยเด่น',
        title: 'จับยี่กี',
        meta: `รอบ ${formatYeekeeRoundLabel(yeekee.roundNumber)} · ${getYeekeeTimeLabel(yeekee)} น.`,
        icon: flagFromRound(yeekee, getAssetUrl, '/img/flag/th.png'),
        variant: 'yeekee',
        live: hasResult,
        wip: false,
        rows: [
          { label: '3 บน', value: disp(resultDigits.threeUp) },
          { label: '2 ล่าง', value: disp(resultDigits.twoDown) },
        ],
      })
    }

    if (!topWidgets.gov.empty) {
      push({
        id: 'featured-gov',
        category: 'หวยเด่น',
        title: topWidgets.gov.title,
        meta: topWidgets.gov.subtitle,
        icon: topWidgets.gov.icon,
        variant: 'gov',
        live: topWidgets.gov.live,
        wip: topWidgets.gov.wip,
        rows: [{ label: 'รางวัลที่ 1', value: topWidgets.gov.first }],
      })
    }

    if (!topWidgets.egov.empty) {
      push({
        id: 'featured-egov',
        category: 'หวยเด่น',
        title: topWidgets.egov.title,
        meta: topWidgets.egov.subtitle,
        icon: topWidgets.egov.icon,
        variant: 'pair',
        live: topWidgets.egov.live,
        wip: topWidgets.egov.wip,
        rows: [
          { label: '3 บน', value: topWidgets.egov.three },
          { label: '2 ล่าง', value: topWidgets.egov.two },
        ],
      })
    }

    if (!topWidgets.gsb.empty) {
      push({
        id: 'featured-gsb',
        category: 'หวยเด่น',
        title: topWidgets.gsb.title,
        meta: topWidgets.gsb.subtitle,
        icon: topWidgets.gsb.icon,
        variant: 'pair',
        live: topWidgets.gsb.live,
        wip: topWidgets.gsb.wip,
        rows: [
          { label: '3 บน', value: topWidgets.gsb.three },
          { label: '2 ล่าง', value: topWidgets.gsb.two },
        ],
      })
    }

    const foreignSection = sectionById('foreign')
    const vipSection = sectionById('vip')
    const foreignCards = foreignSection ? pickSectionCards(foreignSection, 14) : []
    const vipCards = vipSection ? pickSectionCards(vipSection, 14) : []
    const priorityMax = Math.max(foreignCards.length, vipCards.length)

    for (let i = 0; i < priorityMax && slides.length < SHOWCASE_LIMIT; i++) {
      if (i < foreignCards.length && foreignSection) {
        push(sectionCardToShowcaseSlide(foreignSection, foreignCards[i], getAssetUrl))
      }
      if (slides.length >= SHOWCASE_LIMIT) break
      if (i < vipCards.length && vipSection) {
        push(sectionCardToShowcaseSlide(vipSection, vipCards[i], getAssetUrl))
      }
    }

    for (const sectionId of ['set', 'thai-stock', 'stock', 'visa'] as const) {
      if (slides.length >= SHOWCASE_LIMIT) break
      const section = sectionById(sectionId)
      if (!section) continue
      for (const card of pickSectionCards(section, 2)) {
        push(sectionCardToShowcaseSlide(section, card, getAssetUrl))
        if (slides.length >= SHOWCASE_LIMIT) break
      }
    }

    if (slides.length < SHOWCASE_LIMIT) {
      for (const section of sections) {
        if (section.kind === 'yeekee' || !section.cards.length) continue
        for (const card of section.cards) {
          push(sectionCardToShowcaseSlide(section, card, getAssetUrl))
          if (slides.length >= SHOWCASE_LIMIT) break
        }
        if (slides.length >= SHOWCASE_LIMIT) break
      }
    }

    return slides
  }, [topWidgets, sections, latestYeekeeRound, recentYeekeeRounds, assetConfig.CDN_BASE_URL, themeFolder])

  const [showcaseIdx, setShowcaseIdx] = useState(0)
  const [showcasePaused, setShowcasePaused] = useState(false)

  useEffect(() => {
    if (showcaseIdx >= showcaseSlides.length) setShowcaseIdx(0)
  }, [showcaseIdx, showcaseSlides.length])

  useEffect(() => {
    if (showcaseSlides.length <= 1 || showcasePaused) return
    const id = window.setInterval(() => {
      setShowcaseIdx((i) => (i + 1) % showcaseSlides.length)
    }, 4200)
    return () => window.clearInterval(id)
  }, [showcaseSlides.length, showcasePaused])

  const showcaseSlide = showcaseSlides[showcaseIdx] ?? null

  if (!isResultsLayout && authenticated && location.pathname === '/login') {
    return <Navigate to="/" replace />
  }

  const siteName = themeConfig.theme.branding.siteName || 'HuayToday'
  const showcaseFallbackFlag = getAssetUrl('/img/flag/th.png')
  const memberDisplayName = user?.mobile || user?.username || '—'
  const creditValue = loadingWallet ? null : walletService.getDisplayBalance(walletData)
  const creditText = creditValue == null ? '…' : walletService.formatCurrency(creditValue)
  const pointsText = loadingWallet
    ? '…'
    : points.toLocaleString('th-TH', { maximumFractionDigits: 0 })

  const onLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !password || submitting) return
    setSubmitting(true)
    setError('')
    try {
      const res = await authService.login({
        tenantId: themeConfig.tenantId,
        username: username.trim(),
        password,
        rememberMe,
      })
      if (res.success) {
        if (rememberMe) localStorage.setItem('savedUsername', username.trim())
        else localStorage.removeItem('savedUsername')
        localStorage.setItem('rememberMe', rememberMe ? 'true' : 'false')
        navigate('/', { replace: true })
        window.location.reload()
        return
      }
      setError(res.message || res.error || 'เข้าสู่ระบบไม่สำเร็จ')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'เข้าสู่ระบบไม่สำเร็จ')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={`htg-home${isResultsLayout ? ' htg-home--results' : ''}`}>
      {isResultsLayout ? (
        <header className="htg-home-results-toolbar">
          <div className="htg-home-results-toolbar__titles">
            <h2>ผลหวย</h2>
            <p>รูปแบบเดียวกับหน้าแรก · อัปเดตทุก 20 วินาที</p>
          </div>
          <label className="htg-home-results-date">
            <span>วันที่</span>
            <input
              type="date"
              value={selectedDate}
              max={todayBangkokYYYYMMDD()}
              onChange={(e) => setResultsDate(e.target.value || todayBangkokYYYYMMDD())}
            />
          </label>
        </header>
      ) : null}

      {!isResultsLayout ? (
      <div className="htg-home__top">
        <section
          className={`htg-home-login animateMe${authenticated ? ' htg-home-login--member' : ''}`}
          data-animation="fadeInUp"
        >
          {authenticated ? (
            <div className="htg-home-login__member">
              <div className="htg-home-login__member-card">
                <div className="htg-home-login__welcome">
                  <p className="htg-home-login__hello">สวัสดีคุณ</p>
                  <p className="htg-home-login__name">{memberDisplayName}</p>
                </div>
                <div className="htg-home-login__balance">
                  <div className="htg-home-login__balance-tile htg-home-login__balance-tile--credit">
                    <span className="htg-home-login__balance-icon" aria-hidden>
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path
                          fill="currentColor"
                          d="M3 7.5A2.5 2.5 0 0 1 5.5 5h13A2.5 2.5 0 0 1 21 7.5v9A2.5 2.5 0 0 1 18.5 19h-13A2.5 2.5 0 0 1 3 16.5zm14.25 4.25a1.25 1.25 0 1 0 1.25 1.25 1.25 1.25 0 0 0-1.25-1.25z"
                        />
                      </svg>
                    </span>
                    <div className="htg-home-login__balance-meta">
                      <span>เครดิต</span>
                      <strong
                        className="htg-home-login__balance-value"
                        title={creditValue == null ? undefined : `${creditText} บาท`}
                      >
                        <span className="htg-home-login__balance-amount">{creditText}</span>
                        {creditValue != null ? (
                          <span className="htg-home-login__balance-unit">฿</span>
                        ) : null}
                      </strong>
                    </div>
                  </div>
                  <div className="htg-home-login__balance-tile htg-home-login__balance-tile--points">
                    <span className="htg-home-login__balance-icon" aria-hidden>
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path
                          fill="currentColor"
                          d="M12 2.8 14.7 9l6.8.6-5.2 4.5 1.6 6.6L12 17.5 6.1 20.7l1.6-6.6L2.5 9.6 9.3 9z"
                        />
                      </svg>
                    </span>
                    <div className="htg-home-login__balance-meta">
                      <span>คะแนน</span>
                      <strong className="htg-home-login__balance-value" title={pointsText}>
                        <span className="htg-home-login__balance-amount">{pointsText}</span>
                      </strong>
                    </div>
                  </div>
                </div>
                <Link to="/points" className="htg-home-login__footlink">
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                    <path
                      fill="currentColor"
                      d="M12 3.5c-1.8 0-3.2 1.1-3.7 2.7H7.2A2.2 2.2 0 0 0 5 8.4v2.1h2.1v7.9A2.1 2.1 0 0 0 9.2 20.5h5.6a2.1 2.1 0 0 0 2.1-2.1v-7.9H19V8.4a2.2 2.2 0 0 0-2.2-2.2h-1.1C15.2 4.6 13.8 3.5 12 3.5zm0 1.8c.9 0 1.6.5 1.9 1.2h-3.8c.3-.7 1-1.2 1.9-1.2z"
                    />
                  </svg>
                  <span>ดูคะแนนทั้งหมด</span>
                  <svg className="htg-home-login__footlink-chevron" viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                    <path fill="currentColor" d="M9.3 6.3 15 12l-5.7 5.7 1.4 1.4L17.8 12 10.7 4.9z" />
                  </svg>
                </Link>
              </div>

              {isLotteryEnabled || isCasinoEnabled ? (
                <div className="htg-home-login__member-actions">
                  {isLotteryEnabled ? (
                    <Link
                      to="/lobby-huay/listall"
                      className="htg-home-login__member-action htg-home-login__member-action--lottery htg-home-login__member-action--invite"
                    >
                      <span className="htg-home-login__member-action-icon" aria-hidden>
                        <svg className="htg-home-login__action-svg" viewBox="0 0 40 40" width="22" height="22">
                          <defs>
                            <linearGradient id="htgLotTicket" x1="6" y1="8" x2="34" y2="32" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#fff" stopOpacity="0.95" />
                              <stop offset="1" stopColor="#fff" stopOpacity="0.55" />
                            </linearGradient>
                          </defs>
                          <g className="htg-home-login__action-ticket">
                            <path
                              fill="url(#htgLotTicket)"
                              d="M8.5 11.2c0-1.4 1.1-2.5 2.5-2.5h18c1.4 0 2.5 1.1 2.5 2.5v3.1c-1.1.2-1.9 1.2-1.9 2.4s.8 2.2 1.9 2.4v3.7c0 1.4-1.1 2.5-2.5 2.5h-18c-1.4 0-2.5-1.1-2.5-2.5v-3.7c1.1-.2 1.9-1.2 1.9-2.4s-.8-2.2-1.9-2.4v-3.1z"
                            />
                            <path
                              fill="none"
                              stroke="currentColor"
                              strokeOpacity="0.35"
                              strokeWidth="1.2"
                              strokeDasharray="2.2 2.4"
                              d="M16.2 10.2v18.8"
                            />
                            <circle className="htg-home-login__action-ball htg-home-login__action-ball--a" cx="22.2" cy="16.4" r="2.15" fill="currentColor" opacity="0.9" />
                            <circle className="htg-home-login__action-ball htg-home-login__action-ball--b" cx="27.4" cy="16.4" r="2.15" fill="currentColor" opacity="0.72" />
                            <circle className="htg-home-login__action-ball htg-home-login__action-ball--c" cx="24.8" cy="21.8" r="2.15" fill="currentColor" opacity="0.82" />
                          </g>
                          <path
                            className="htg-home-login__action-spark htg-home-login__action-spark--a"
                            fill="currentColor"
                            d="M12.4 8.2 13 10.1l1.9.6-1.9.6-.6 1.9-.6-1.9-1.9-.6 1.9-.6z"
                          />
                          <path
                            className="htg-home-login__action-spark htg-home-login__action-spark--b"
                            fill="currentColor"
                            d="M31.6 26.2 32.1 27.6l1.4.4-1.4.4-.5 1.4-.5-1.4-1.4-.4 1.4-.4z"
                          />
                        </svg>
                      </span>
                      <span className="htg-home-login__member-action-body">
                        <strong>เข้าแทงหวย</strong>
                        <small>เลือกหวยที่เปิดรับแทง</small>
                      </span>
                      <svg className="htg-home-login__member-action-arrow" viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                        <path fill="currentColor" d="M9.3 6.3 15 12l-5.7 5.7 1.4 1.4L17.8 12 10.7 4.9z" />
                      </svg>
                    </Link>
                  ) : null}
                  {isCasinoEnabled ? (
                    <Link
                      to="/lobby-casino"
                      className="htg-home-login__member-action htg-home-login__member-action--casino htg-home-login__member-action--invite"
                    >
                      <span className="htg-home-login__member-action-icon" aria-hidden>
                        <svg className="htg-home-login__action-svg" viewBox="0 0 40 40" width="22" height="22">
                          <g className="htg-home-login__action-chip">
                            <circle cx="15.5" cy="22.5" r="9.2" fill="currentColor" opacity="0.22" />
                            <circle cx="15.5" cy="22.5" r="9.2" fill="none" stroke="currentColor" strokeWidth="1.8" opacity="0.85" />
                            <circle cx="15.5" cy="22.5" r="5.6" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.55" strokeDasharray="2.4 2.1" />
                            <circle cx="15.5" cy="22.5" r="2.4" fill="currentColor" opacity="0.9" />
                          </g>
                          <g className="htg-home-login__action-dice">
                            <rect
                              x="20.2"
                              y="8.8"
                              width="13.2"
                              height="13.2"
                              rx="2.6"
                              fill="#fff"
                              fillOpacity="0.92"
                              stroke="currentColor"
                              strokeOpacity="0.25"
                              strokeWidth="0.8"
                            />
                            <circle cx="24.2" cy="12.8" r="1.35" fill="currentColor" opacity="0.85" />
                            <circle cx="29.4" cy="12.8" r="1.35" fill="currentColor" opacity="0.85" />
                            <circle cx="26.8" cy="15.4" r="1.35" fill="currentColor" opacity="0.85" />
                            <circle cx="24.2" cy="18" r="1.35" fill="currentColor" opacity="0.85" />
                            <circle cx="29.4" cy="18" r="1.35" fill="currentColor" opacity="0.85" />
                          </g>
                        </svg>
                      </span>
                      <span className="htg-home-login__member-action-body">
                        <strong>เข้าเล่นเกมส์</strong>
                        <small>คาสิโน · สล็อต · อื่นๆ</small>
                      </span>
                      <svg className="htg-home-login__member-action-arrow" viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                        <path fill="currentColor" d="M9.3 6.3 15 12l-5.7 5.7 1.4 1.4L17.8 12 10.7 4.9z" />
                      </svg>
                    </Link>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <form className="htg-home-login__stack" onSubmit={(e) => void onLogin(e)}>
              <header className="htg-home-login__head">
                <div className="htg-home-login__avatar" aria-hidden>
                  <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden>
                    <path
                      fill="currentColor"
                      d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.5 4.5 0 0 0 12 12zm0 2.25c-3.6 0-6.75 1.8-6.75 4.05V20h13.5v-1.7c0-2.25-3.15-4.05-6.75-4.05z"
                    />
                  </svg>
                </div>
                <div className="htg-home-login__head-text">
                  <h4>เข้าสู่ระบบ</h4>
                  <p>สมาชิก {siteName}</p>
                </div>
              </header>

              <div className="htg-home-login__form">
                {error ? (
                  <div className="htg-home-login__error" role="alert">
                    {error}
                  </div>
                ) : null}

                <label className="htg-home-login__field">
                  <span className="htg-home-login__field-label">ชื่อผู้ใช้งาน</span>
                  <span className="htg-home-login__field-box">
                    <svg className="htg-home-login__icon" viewBox="0 0 24 24" aria-hidden>
                      <path
                        fill="currentColor"
                        d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.5 4.5 0 0 0 12 12zm0 2.25c-3.6 0-6.75 1.8-6.75 4.05V20h13.5v-1.7c0-2.25-3.15-4.05-6.75-4.05z"
                      />
                    </svg>
                    <input
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      placeholder="กรอกชื่อผู้ใช้"
                      disabled={submitting}
                    />
                  </span>
                </label>

                <label className="htg-home-login__field">
                  <span className="htg-home-login__field-label">รหัสผ่าน</span>
                  <span className="htg-home-login__field-box">
                    <svg className="htg-home-login__icon" viewBox="0 0 24 24" aria-hidden>
                      <path
                        fill="currentColor"
                        d="M17.25 10.5V8.25A5.25 5.25 0 0 0 12 3a5.25 5.25 0 0 0-5.25 5.25V10.5H6A1.5 1.5 0 0 0 4.5 12v7.5A1.5 1.5 0 0 0 6 21h12a1.5 1.5 0 0 0 1.5-1.5V12a1.5 1.5 0 0 0-1.5-1.5zm-8.25 0V8.25a3 3 0 0 1 6 0V10.5z"
                      />
                    </svg>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      placeholder="กรอกรหัสผ่าน"
                      disabled={submitting}
                    />
                    <button
                      type="button"
                      className="htg-home-login__toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                      tabIndex={-1}
                    >
                      {showPassword ? 'ซ่อน' : 'แสดง'}
                    </button>
                  </span>
                </label>

                <div className="htg-home-login__row">
                  <label className="htg-home-login__remember">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={submitting}
                    />
                    <span className="htg-home-login__check" aria-hidden />
                    จดจำชื่อผู้ใช้
                  </label>
                </div>

                <button type="submit" className="htg-home-login__submit" disabled={submitting}>
                  <span>{submitting ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}</span>
                  {!submitting ? (
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
                      <path
                        fill="currentColor"
                        d="M13.5 5.25 19.5 12l-6 6.75V14.25H4.5v-4.5h9z"
                      />
                    </svg>
                  ) : null}
                </button>

                <div className="htg-home-login__footer-links">
                  <Link to="/register" className="htg-home-login__text-link">
                    สมัครสมาชิก
                  </Link>
                  <span aria-hidden>·</span>
                  <button type="button" className="htg-home-login__text-link" disabled>
                    ลืมรหัสผ่าน
                  </button>
                </div>
              </div>
            </form>
          )}
        </section>

        <aside className="htg-home-showcase animateMe" data-animation="fadeInUp" aria-label="ไฮไลท์หวยวันนี้">
          <span className="htg-home-showcase__orb htg-home-showcase__orb--a" aria-hidden />
          <span className="htg-home-showcase__orb htg-home-showcase__orb--b" aria-hidden />
          <span className="htg-home-showcase__spark" aria-hidden />

          <div className="htg-home-showcase__inner">
            <p className="htg-home-showcase__eyebrow">{siteName}</p>
            <h2 className="htg-home-showcase__title">
              {authenticated ? 'พร้อมลุ้นรางวัลวันนี้' : 'แทงหวยออนไลน์ จ่ายจริง'}
            </h2>
            <p className="htg-home-showcase__lead">ผลอัปเดตทุก 20 วินาที · สลับแสดงหลายหมวด</p>

            <div
              className={`htg-home-showcase__rotator${showcasePaused ? ' is-paused' : ''}`}
              onMouseEnter={() => setShowcasePaused(true)}
              onMouseLeave={() => setShowcasePaused(false)}
              onFocusCapture={() => setShowcasePaused(true)}
              onBlurCapture={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setShowcasePaused(false)
                }
              }}
            >
              <div className="htg-home-showcase__rotator-head">
                <span>ผลหวยวันนี้</span>
                {showcaseSlides.length > 0 ? (
                  <em>
                    {showcaseIdx + 1}/{showcaseSlides.length}
                  </em>
                ) : null}
              </div>

              <div className="htg-home-showcase__stage">
                {resultsLoading && showcaseSlides.length === 0 ? (
                  <p className="htg-home-showcase__empty">กำลังโหลดผลหวย…</p>
                ) : showcaseSlide ? (
                  <article
                    key={showcaseSlide.id}
                    className={`htg-home-showcase__slide htg-home-showcase__slide--${showcaseSlide.variant}${showcaseSlide.live ? ' is-live' : ''}${showcaseSlide.wip ? ' is-wip' : ''}`}
                  >
                    <div className="htg-home-showcase__slide-top">
                      <div className="htg-home-showcase__slide-icon">
                        <img
                          src={showcaseSlide.icon || showcaseFallbackFlag}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            if (e.currentTarget.src !== showcaseFallbackFlag) {
                              e.currentTarget.src = showcaseFallbackFlag
                            }
                          }}
                        />
                      </div>
                      <div className="htg-home-showcase__slide-info">
                        <div className="htg-home-showcase__slide-head">
                          <span className="htg-home-showcase__slide-cat">{showcaseSlide.category}</span>
                          <div className="htg-home-showcase__slide-tags">
                            {showcaseSlide.meta && showcaseSlide.meta !== '—' ? (
                              <span className="htg-home-showcase__slide-draw">{showcaseSlide.meta}</span>
                            ) : null}
                            {showcaseSlide.live ? (
                              <em className="htg-home-showcase__live-badge">LIVE</em>
                            ) : null}
                          </div>
                        </div>
                        <h3>{showcaseSlide.title}</h3>
                      </div>
                    </div>
                    <div
                      className={`htg-home-showcase__slide-nums${showcaseSlide.variant === 'gov' ? ' is-gov' : showcaseSlide.variant === 'set' ? ' is-set' : ''}`}
                    >
                      {showcaseSlide.rows.map((row) => (
                        <span key={`${showcaseSlide.id}-${row.label}`}>
                          <em>{row.label}</em>
                          <b>{row.value}</b>
                        </span>
                      ))}
                    </div>
                  </article>
                ) : (
                  <p className="htg-home-showcase__empty">ยังไม่มีผลหวยในวันนี้</p>
                )}
              </div>
            </div>

            <Link to="/lobby-huay/listall" className="htg-home-showcase__cta">
              แทงหวยเลย
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                <path fill="currentColor" d="M13.5 5.25 19.5 12l-6 6.75V14.25H4.5v-4.5h9z" />
              </svg>
            </Link>
          </div>
        </aside>
      </div>
      ) : null}

      <section
        className="htg-home-section htg-home-section--featured animateMe"
        data-animation="fadeInUp"
        aria-label="ผลหวยเด่น"
      >
        <header className="htg-home-section__rail">
          <div className="htg-home-section__rail-main">
            <span className="htg-home-section__mark" aria-hidden />
            <div className="htg-home-section__titles">
              <h5>ผลหวยเด่น</h5>
              <p>รัฐบาล · ยี่กี · ธ.ก.ส. · ออมสิน</p>
            </div>
          </div>
          <span className="htg-home-section__badge">
            <b>4</b>
            <em>รายการ</em>
          </span>
        </header>
        <div className="htg-home-section__body">
          {resultsLoading ? <div className="htg-results__status">กำลังโหลดผลหวย…</div> : null}
          {poolError ? <div className="htg-results__status htg-results__status--err">{poolError}</div> : null}

          <div className="htg-results">
          <article
            className={`htg-tile htg-tile--gov htg-tile--featured${topWidgets.gov.live ? ' is-live' : ''}${topWidgets.gov.empty ? ' is-empty' : ''}${topWidgets.gov.closed ? ' is-closed' : ''}${topWidgets.gov.wip ? ' is-wip' : ''}`}
          >
            <span className="htg-tile__shine" aria-hidden />
            <header className="htg-tile__head">
              <div className="htg-tile__identity">
                <div className="htg-tile__icon">
                  <img src={topWidgets.gov.icon} alt="" loading="lazy" />
                </div>
                <div>
                  <p className="htg-tile__kicker">Hot · หวยยอดนิยม</p>
                  <h3>{topWidgets.gov.title}</h3>
                  <span className={`htg-tile__draw${topWidgets.gov.wip ? ' wip_wup' : ''}`}>
                    {topWidgets.gov.subtitle}
                  </span>
                </div>
              </div>
              {topWidgets.gov.empty ? (
                <em className="htg-tile__badge htg-tile__badge--wait">ยังไม่มีงวด</em>
              ) : topWidgets.gov.wip ? (
                <em className="htg-tile__badge wip_wup">ใกล้ปิดรับ</em>
              ) : topWidgets.gov.live ? (
                <em className="htg-tile__badge">ออกผลแล้ว</em>
              ) : topWidgets.gov.closed ? (
                <em className="htg-tile__badge htg-tile__badge--closed">ปิดรับแทง</em>
              ) : (
                <em className="htg-tile__badge">เปิดรับแทง</em>
              )}
            </header>
            <div className="htg-tile__body htg-tile__body--gov">
              {topWidgets.gov.empty ? (
                <div className="htg-tile__empty" role="status">
                  <span>ยังไม่มีรอบในวันนี้</span>
                  <div className="htg-tile__prize">
                    <em>รางวัลที่ 1</em>
                    <strong>—</strong>
                  </div>
                  <div className="htg-tile__row">
                    <div className="htg-tile__cell">
                      <em>3 ตัวบน</em>
                      <b>—</b>
                    </div>
                    <div className="htg-tile__cell">
                      <em>2 ตัวล่าง</em>
                      <b>—</b>
                    </div>
                  </div>
                  <div className="htg-tile__bottom3">
                    <em>3 ตัวล่าง</em>
                    <b>—</b>
                  </div>
                </div>
              ) : topWidgets.gov.cancelled ? (
                <p className="htg-tile__cancelled">ยกเลิก</p>
              ) : (
                <>
                  <div className={`htg-tile__prize${topWidgets.gov.live ? ' has-num' : ''}${topWidgets.gov.wip && !topWidgets.gov.live ? ' is-wip-zone' : ''}`}>
                    <em>รางวัลที่ 1</em>
                    <strong className={topWidgets.gov.wip && topWidgets.gov.first === 'รอผล' ? 'wip_wup' : undefined}>
                      {topWidgets.gov.first}
                    </strong>
                    {!topWidgets.gov.live ? (
                      <p className="htg-tile__hook">ลุ้นรางวัลใหญ่ · จ่ายจริงทุกงวด</p>
                    ) : null}
                  </div>
                  <div className={`htg-tile__row${topWidgets.gov.wip ? ' is-wip-zone' : ''}`}>
                    <div
                      className={`htg-tile__cell${topWidgets.gov.threeUp !== 'รอผล' && topWidgets.gov.threeUp !== '—' ? ' has-num' : ''}`}
                    >
                      <em>3 ตัวบน</em>
                      <b className={topWidgets.gov.wip && topWidgets.gov.threeUp === 'รอผล' ? 'wip_wup' : undefined}>
                        {topWidgets.gov.threeUp}
                      </b>
                    </div>
                    <div
                      className={`htg-tile__cell${topWidgets.gov.twoDown !== 'รอผล' && topWidgets.gov.twoDown !== '—' ? ' has-num' : ''}`}
                    >
                      <em>2 ตัวล่าง</em>
                      <b className={topWidgets.gov.wip && topWidgets.gov.twoDown === 'รอผล' ? 'wip_wup' : undefined}>
                        {topWidgets.gov.twoDown}
                      </b>
                    </div>
                  </div>
                  <div
                    className={`htg-tile__bottom3${topWidgets.gov.bottomPending ? ' is-pending' : ' has-num'}${topWidgets.gov.wip ? ' is-wip-zone' : ''}`}
                  >
                    <em>3 ตัวล่าง</em>
                    {topWidgets.gov.bottomPending ? (
                      <b className={topWidgets.gov.wip && topWidgets.gov.bottomPending === 'รอผล' ? 'wip_wup' : undefined}>
                        {topWidgets.gov.bottomPending}
                      </b>
                    ) : (
                      <div className="htg-tile__bottom3-grid" aria-label={`3 ตัวล่าง ${topWidgets.gov.bottomNumbers.join(', ')}`}>
                        {topWidgets.gov.bottomNumbers.map((n, i) => (
                          <span key={`${n}-${i}`}>{n}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
              <Link to="/lobby-huay/listall" className="htg-tile__cta">
                แทงเลย
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                  <path fill="currentColor" d="M13.5 5.25 19.5 12l-6 6.75V14.25H4.5v-4.5h9z" />
                </svg>
              </Link>
            </div>
          </article>

          <article
            className={`htg-tile htg-tile--yeekee${topWidgets.yeekee.live ? ' is-live' : ''}${topWidgets.yeekee.empty ? ' is-empty' : ''}${topWidgets.yeekee.closed ? ' is-closed' : ''}${topWidgets.yeekee.wip ? ' is-wip' : ''}`}
          >
            <span className="htg-yeekee__aura" aria-hidden />
            <header className="htg-yeekee__top">
              <div className="htg-yeekee__brand">
                <div className="htg-yeekee__icon">
                  <img src={topWidgets.yeekee.icon} alt="" loading="lazy" />
                </div>
                <div>
                  <span className="htg-yeekee__kicker">Yeekee</span>
                  <h3 className="htg-yeekee__title">{topWidgets.yeekee.title}</h3>
                </div>
              </div>
              {topWidgets.yeekee.empty ? (
                <em className="htg-yeekee__pill htg-yeekee__pill--wait">ยังไม่มีงวด</em>
              ) : topWidgets.yeekee.wip ? (
                <em className="htg-yeekee__pill htg-yeekee__pill--wip wip_wup">ใกล้ปิดรับ</em>
              ) : topWidgets.yeekee.live ? (
                <em className="htg-yeekee__pill htg-yeekee__pill--live">
                  <i aria-hidden />
                  ผลล่าสุด
                </em>
              ) : topWidgets.yeekee.closed ? (
                <em className="htg-yeekee__pill htg-yeekee__pill--closed">ปิดรับแทง</em>
              ) : (
                <em className="htg-yeekee__pill">เปิดรับแทง</em>
              )}
            </header>

            {topWidgets.yeekee.empty ? (
              <div className="htg-yeekee__empty">
                <p>ยังไม่มีรอบในวันนี้</p>
                <div className="htg-yeekee__hero htg-yeekee__hero--placeholder">
                  <div className="htg-yeekee__prize">
                    <em>3 ตัวบน</em>
                    <strong>—</strong>
                  </div>
                  <span className="htg-yeekee__hero-sep" aria-hidden />
                  <div className="htg-yeekee__prize">
                    <em>2 ตัวล่าง</em>
                    <strong>—</strong>
                  </div>
                </div>
              </div>
            ) : topWidgets.yeekee.cancelled ? (
              <div className="htg-yeekee__empty">
                <p className="htg-yeekee__cancelled">ยกเลิก</p>
              </div>
            ) : (
              <>
                <div className="htg-yeekee__live">
                  <div className="htg-yeekee__chips">
                    <span className={`htg-yeekee__chip htg-yeekee__chip--round${topWidgets.yeekee.wip ? ' wip_wup' : ''}`}>
                      รอบ {topWidgets.yeekee.roundLabel || '—'}
                    </span>
                    <span className={`htg-yeekee__chip htg-yeekee__chip--time${topWidgets.yeekee.wip ? ' wip_wup' : ''}`}>
                      {topWidgets.yeekee.timeLabel} น.
                    </span>
                  </div>
                  <div className={`htg-yeekee__hero${topWidgets.yeekee.wip ? ' is-wip-zone' : ''}${topWidgets.yeekee.live ? ' is-hot' : ''}`}>
                    <div className={`htg-yeekee__prize${topWidgets.yeekee.live ? ' has-num' : ''}`}>
                      <em>3 ตัวบน</em>
                      <strong className={topWidgets.yeekee.wip && topWidgets.yeekee.three === 'รอผล' ? 'wip_wup' : undefined}>
                        {topWidgets.yeekee.three}
                      </strong>
                    </div>
                    <span className="htg-yeekee__hero-sep" aria-hidden />
                    <div className={`htg-yeekee__prize${topWidgets.yeekee.live ? ' has-num' : ''}`}>
                      <em>2 ตัวล่าง</em>
                      <strong className={topWidgets.yeekee.wip && topWidgets.yeekee.two === 'รอผล' ? 'wip_wup' : undefined}>
                        {topWidgets.yeekee.two}
                      </strong>
                    </div>
                  </div>
                </div>

                {topWidgets.yeekee.recent.length > 0 ? (
                  <div className="htg-yeekee__history">
                    <div className="htg-yeekee__history-bar">
                      <span>รอบก่อนหน้า</span>
                      <em>{topWidgets.yeekee.recent.length} รอบ</em>
                    </div>
                    <div className="htg-yeekee__history-grid" role="list">
                      {topWidgets.yeekee.recent.map((row) => (
                        <div
                          key={row.id}
                          role="listitem"
                          className={`htg-yeekee__hist${row.ready ? ' is-hit' : ''}`}
                        >
                          <span className="htg-yeekee__hist-round">
                            <b>{row.label || '—'}</b>
                            <i>{row.time}</i>
                          </span>
                          <span className="htg-yeekee__hist-nums" aria-label={`3 บน ${row.three} 2 ล่าง ${row.two}`}>
                            <span>
                              <em>3</em>
                              {row.three}
                            </span>
                            <span>
                              <em>2</em>
                              {row.two}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </article>

          <article
            className={`htg-tile htg-tile--egov${topWidgets.egov.live ? ' is-live' : ''}${topWidgets.egov.empty ? ' is-empty' : ''}${topWidgets.egov.closed ? ' is-closed' : ''}${topWidgets.egov.wip ? ' is-wip' : ''}`}
          >
            <header className="htg-tile__head">
              <div className="htg-tile__identity">
                <div className="htg-tile__icon">
                  <img src={topWidgets.egov.icon} alt="" loading="lazy" />
                </div>
                <div>
                  <p className="htg-tile__kicker">BAAC</p>
                  <h3>{topWidgets.egov.title}</h3>
                  <span className={`htg-tile__draw${topWidgets.egov.wip ? ' wip_wup' : ''}`}>
                    {topWidgets.egov.subtitle}
                  </span>
                </div>
              </div>
              {topWidgets.egov.empty ? (
                <em className="htg-tile__badge htg-tile__badge--wait">ยังไม่มีงวด</em>
              ) : topWidgets.egov.closed ? (
                <em className="htg-tile__badge htg-tile__badge--closed">ปิดรับแทง</em>
              ) : topWidgets.egov.live ? (
                <em className="htg-tile__badge">ออกผลแล้ว</em>
              ) : null}
            </header>
            {topWidgets.egov.empty ? (
              <div className="htg-tile__body htg-tile__body--pair">
                <div className="htg-tile__empty--pair" role="status">
                  ยังไม่มีรอบในวันนี้
                </div>
              </div>
            ) : topWidgets.egov.cancelled ? (
              <div className="htg-tile__body">
                <p className="htg-tile__cancelled">ยกเลิก</p>
              </div>
            ) : (
              <div className={`htg-tile__body htg-tile__body--pair${topWidgets.egov.wip ? ' is-wip-zone' : ''}`}>
                <div className={`htg-tile__cell${topWidgets.egov.live ? ' has-num' : ''}`}>
                  <em>3 ตัวบน</em>
                  <b className={topWidgets.egov.wip && topWidgets.egov.three === 'รอผล' ? 'wip_wup' : undefined}>
                    {topWidgets.egov.three}
                  </b>
                </div>
                <div className={`htg-tile__cell${topWidgets.egov.live ? ' has-num' : ''}`}>
                  <em>2 ตัวล่าง</em>
                  <b className={topWidgets.egov.wip && topWidgets.egov.two === 'รอผล' ? 'wip_wup' : undefined}>
                    {topWidgets.egov.two}
                  </b>
                </div>
              </div>
            )}
          </article>

          <article
            className={`htg-tile htg-tile--gsb${topWidgets.gsb.live ? ' is-live' : ''}${topWidgets.gsb.empty ? ' is-empty' : ''}${topWidgets.gsb.closed ? ' is-closed' : ''}${topWidgets.gsb.wip ? ' is-wip' : ''}`}
          >
            <header className="htg-tile__head">
              <div className="htg-tile__identity">
                <div className="htg-tile__icon">
                  <img src={topWidgets.gsb.icon} alt="" loading="lazy" />
                </div>
                <div>
                  <p className="htg-tile__kicker">GSB</p>
                  <h3>{topWidgets.gsb.title}</h3>
                  <span className={`htg-tile__draw${topWidgets.gsb.wip ? ' wip_wup' : ''}`}>
                    {topWidgets.gsb.subtitle}
                  </span>
                </div>
              </div>
              {topWidgets.gsb.empty ? (
                <em className="htg-tile__badge htg-tile__badge--wait">ยังไม่มีงวด</em>
              ) : topWidgets.gsb.closed ? (
                <em className="htg-tile__badge htg-tile__badge--closed">ปิดรับแทง</em>
              ) : topWidgets.gsb.live ? (
                <em className="htg-tile__badge">ออกผลแล้ว</em>
              ) : null}
            </header>
            {topWidgets.gsb.empty ? (
              <div className="htg-tile__body htg-tile__body--pair">
                <div className="htg-tile__empty--pair" role="status">
                  ยังไม่มีรอบในวันนี้
                </div>
              </div>
            ) : topWidgets.gsb.cancelled ? (
              <div className="htg-tile__body">
                <p className="htg-tile__cancelled">ยกเลิก</p>
              </div>
            ) : (
              <div className={`htg-tile__body htg-tile__body--pair${topWidgets.gsb.wip ? ' is-wip-zone' : ''}`}>
                <div className={`htg-tile__cell${topWidgets.gsb.live ? ' has-num' : ''}`}>
                  <em>3 ตัวบน</em>
                  <b className={topWidgets.gsb.wip && topWidgets.gsb.three === 'รอผล' ? 'wip_wup' : undefined}>
                    {topWidgets.gsb.three}
                  </b>
                </div>
                <div className={`htg-tile__cell${topWidgets.gsb.live ? ' has-num' : ''}`}>
                  <em>2 ตัวล่าง</em>
                  <b className={topWidgets.gsb.wip && topWidgets.gsb.two === 'รอผล' ? 'wip_wup' : undefined}>
                    {topWidgets.gsb.two}
                  </b>
                </div>
              </div>
            )}
          </article>
          </div>
        </div>
      </section>

      {resultsLoading && visibleSections.every((s) => s.cards.length === 0) ? (
        <div className="htg-home-section__loading">กำลังโหลดผลหวยตามประเภท…</div>
      ) : null}

      {visibleSections.map((section) => (
        <section
          key={section.id}
          className={`htg-home-section animateMe${section.tone === 'dark' ? ' htg-home-section--dark' : ''}`}
          data-animation="fadeInUp"
        >
          <header className="htg-home-section__rail">
            <div className="htg-home-section__rail-main">
              <span className="htg-home-section__mark" aria-hidden />
              <div className="htg-home-section__titles">
                <h5>{section.title}</h5>
                {section.dateLabel && section.dateLabel !== 'งวด' ? <p>{section.dateLabel}</p> : null}
              </div>
            </div>
            <span className="htg-home-section__badge">
              {section.cards.length > 0 ? (
                <>
                  <b>{section.cards.length}</b>
                  <em>รายการ</em>
                </>
              ) : (
                <em>ว่าง</em>
              )}
            </span>
          </header>
          <div className="htg-home-section__body">
            {section.cards.length ? (
              <div
                className={`htg-home-cards${
                  section.kind === 'set'
                    ? ' htg-home-cards--set'
                    : section.kind === 'yeekee'
                      ? ' htg-home-cards--yeekee'
                      : ''
                }`}
              >
                {section.cards.map((card, idx) => {
                  const isSet = section.kind === 'set'
                  const isYeekee = section.kind === 'yeekee'
                  const tone = card.tone || 'wait'
                  const pendingA = card.a === 'รอผล' || card.a === '-' || card.a === '—'
                  const pendingB = card.b === 'รอผล' || card.b === '-' || card.b === '—'
                  const setChars =
                    !pendingA && card.a && !/[,，]/.test(card.a) ? Array.from(card.a) : null
                  const toneLabel = card.wip
                    ? 'ใกล้ปิดรับ'
                    : tone === 'ready'
                      ? 'ประกาศผลแล้ว'
                      : tone === 'closed'
                        ? 'ปิดรับแทง'
                        : tone === 'cancelled'
                          ? 'ยกเลิก'
                          : tone === 'open'
                            ? 'เปิดรับ'
                            : null
                  return (
                    <Link
                      key={card.id}
                      to="/lobby-huay/listall"
                      className={`htg-home-card${isSet ? ' htg-home-card--set' : ''}${isYeekee ? ' htg-home-card--yeekee' : ''} htg-home-card--${tone}${card.wip ? ' is-wip' : ''}`}
                      style={{ ['--htg-card-i' as string]: Math.min(idx, 24) }}
                    >
                      {isYeekee ? (
                        <div className="htg-home-card__yk-top">
                          <strong className={card.wip ? 'wip_wup' : undefined}>{card.name}</strong>
                          {card.drawLabel ? (
                            <span className={card.wip ? 'wip_wup' : undefined}>{card.drawLabel}</span>
                          ) : null}
                        </div>
                      ) : (
                        <div className="htg-home-card__top">
                          <div className={`htg-home-card__flag${card.wip ? ' wip_wup' : ''}`}>
                            {card.flag ? (
                              <img src={card.flag} alt="" loading="lazy" />
                            ) : (
                              <span aria-hidden>•</span>
                            )}
                          </div>
                          <div className="htg-home-card__meta">
                            <div className={`htg-home-card__name${card.wip ? ' wip_wup' : ''}`}>
                              {card.name}
                            </div>
                            {card.drawLabel ? (
                              <div className={`htg-home-card__draw${card.wip ? ' wip_wup' : ''}`}>
                                {card.drawLabel}
                              </div>
                            ) : null}
                          </div>
                          {toneLabel ? (
                            <em
                              className={`htg-home-card__status ${card.wip ? 'htg-home-card__status--open wip_wup' : `htg-home-card__status--${tone}`}`}
                            >
                              {toneLabel}
                            </em>
                          ) : (
                            <span className="htg-home-card__go" aria-hidden>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                                <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                          )}
                        </div>
                      )}
                      {isSet ? (
                        <div
                          className={`htg-home-card__set${pendingA ? ' is-pending' : ' has-num'}${card.wip ? ' is-wip-zone' : ''}`}
                        >
                          <em>{card.labelA || 'เลขชุด 4 ตัว'}</em>
                          {setChars ? (
                            <b className="htg-home-card__digits" aria-label={card.a}>
                              {setChars.map((ch, i) => (
                                <span key={`${ch}-${i}`}>{ch}</span>
                              ))}
                            </b>
                          ) : (
                            <b className={card.wip && pendingA ? 'wip_wup' : undefined}>{card.a || 'รอผล'}</b>
                          )}
                        </div>
                      ) : (
                        <div className={`htg-home-card__nums${card.wip ? ' is-wip-zone' : ''}`}>
                          <div className={pendingA ? 'is-pending' : 'has-num'}>
                            <em>{card.labelA || '3 ตัวบน'}</em>
                            <b className={card.wip && pendingA ? 'wip_wup' : undefined}>{card.a}</b>
                          </div>
                          <div className={pendingB ? 'is-pending' : 'has-num'}>
                            <em>{card.labelB || '2 ตัวล่าง'}</em>
                            <b className={card.wip && pendingB ? 'wip_wup' : undefined}>{card.b}</b>
                          </div>
                        </div>
                      )}
                    </Link>
                  )
                })}
              </div>
            ) : (
              <div className="htg-home-section__empty">
                {resultsLoading ? 'กำลังโหลด…' : section.emptyHint || 'ไม่มีรอบวันนี้'}
              </div>
            )}
          </div>
        </section>
      ))}
    </div>
  )
}

export default HomePage
